import React, { useState } from "react";
import styled from "styled-components";
import { sampleFellows } from "../../utils/fellowsData";
import { useSelector } from "react-redux";
import { selectUser } from "../../features/userSlice";

export const FellowsPage = () => {
  const currentUser = useSelector(selectUser);
  const [activeTab, setActiveTab] = useState("Suggested Fellows");
  const [searchQuery, setSearchQuery] = useState("");
  const [connectedIds, setConnectedIds] = useState(["f1"]);

  const tabs = [
    "Suggested Fellows",
    "My Fellows",
    "Friend Requests",
    "Online Fellows",
    "Recent Connections",
    "Discover Fellows",
    "University Fellows",
    "Faculty/Class Fellows",
    "Alumni Fellows",
  ];

  const handleConnectToggle = (id) => {
    if (connectedIds.includes(id)) {
      setConnectedIds(connectedIds.filter((item) => item !== id));
    } else {
      setConnectedIds([...connectedIds, id]);
    }
  };

  const filteredFellows = sampleFellows.filter((fellow) => {
    const matchesSearch =
      fellow.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      fellow.username.toLowerCase().includes(searchQuery.toLowerCase()) ||
      fellow.university.toLowerCase().includes(searchQuery.toLowerCase()) ||
      fellow.faculty.toLowerCase().includes(searchQuery.toLowerCase());

    if (!matchesSearch) return false;

    if (activeTab === "Online Fellows") return fellow.isOnline;
    if (activeTab === "My Fellows") return connectedIds.includes(fellow.id);
    if (activeTab === "Suggested Fellows") return !connectedIds.includes(fellow.id);
    return true;
  });

  return (
    <PageWrapper>
      <HeaderBanner>
        <div className="banner-content">
          <h1>Fellows Network</h1>
          <p>Join over 50,000 Fellows. People are the heart of Fellora.</p>
        </div>
      </HeaderBanner>

      <MainContainer>
        {/* Search Input Box with SVG Icon */}
        <SearchFilterBox>
          <div className="search-input-wrapper">
            <svg className="search-icon" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#1DA1F2" strokeWidth="2.5">
              <circle cx="11" cy="11" r="8"/>
              <path d="M21 21l-4.35-4.35"/>
            </svg>
            <input
              type="text"
              placeholder="Search Fellows by name, @username, university, or faculty..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            {searchQuery && (
              <button className="clear-btn" onClick={() => setSearchQuery("")}>✕</button>
            )}
          </div>
        </SearchFilterBox>

        {/* Scrollable Filter Tabs Bar */}
        <TabsContainer>
          <div className="tabs-scroll">
            {tabs.map((tab) => (
              <TabButton
                key={tab}
                active={activeTab === tab}
                onClick={() => setActiveTab(tab)}
              >
                {tab}
              </TabButton>
            ))}
          </div>
        </TabsContainer>

        {/* Fellow Cards Responsive Grid */}
        <GridContainer>
          {filteredFellows.map((fellow) => {
            const isConnected = connectedIds.includes(fellow.id);
            return (
              <FellowCard key={fellow.id}>
                <CardTop>
                  <AvatarWrapper>
                    <img src={fellow.avatar} alt={fellow.name} />
                    {fellow.isOnline && <OnlineDot title="Online now" />}
                  </AvatarWrapper>

                  <CardBody>
                    <NameRow>
                      <h3>{fellow.name}</h3>
                    </NameRow>
                    <Handle>{fellow.username}</Handle>

                    <DetailsMeta>
                      {/* University Icon */}
                      <div className="meta-item uni">
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M12 3L1 9l11 6 9-4.91V17h2V9L12 3zM5 13.18v4L12 21l7-3.82v-4L12 17l-7-3.82z"/>
                        </svg>
                        <strong>{fellow.university}</strong>
                      </div>

                      {/* Faculty Icon */}
                      <div className="meta-item">
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M5 13.18v4L12 21l7-3.82v-4L12 17l-7-3.82zM12 3L1 9l11 6 9-4.91V17h2V9L12 3z"/>
                        </svg>
                        <span>{fellow.faculty}</span>
                      </div>

                      {/* Mutual Fellows Icon */}
                      <div className="meta-item mutual">
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M16 11c1.66 0 2.99-1.34 2.99-3S17.66 5 16 5c-1.66 0-3 1.34-3 3s1.34 3 3 3zm-8 0c1.66 0 2.99-1.34 2.99-3S9.66 5 8 5C6.34 5 5 6.34 5 8s1.34 3 3 3zm0 2c-2.33 0-7 1.17-7 3.5V19h14v-2.5c0-2.33-4.67-3.5-7-3.5z"/>
                        </svg>
                        <span>{fellow.mutualCount} Mutual Fellows</span>
                      </div>
                    </DetailsMeta>

                    <BioText>{fellow.bio}</BioText>
                  </CardBody>
                </CardTop>

                <CardFooter>
                  <ConnectButton
                    connected={isConnected}
                    onClick={() => handleConnectToggle(fellow.id)}
                  >
                    {isConnected ? "✓ Connected" : "+ Connect"}
                  </ConnectButton>
                </CardFooter>
              </FellowCard>
            );
          })}
        </GridContainer>

        {filteredFellows.length === 0 && (
          <EmptyState>
            <h3>No Fellows found matching "{searchQuery}"</h3>
            <p>Try searching for a different university, name, or course.</p>
          </EmptyState>
        )}
      </MainContainer>
    </PageWrapper>
  );
};

/* ─── Responsive & Luxury Styled Components ─── */

const PageWrapper = styled.div`
  width: 100%;
  min-height: calc(100vh - 60px);
  background-color: #faf7f2;
  padding-bottom: 3rem;
`;

const HeaderBanner = styled.div`
  background: linear-gradient(135deg, #0a1428 0%, #172a4d 100%);
  color: white;
  padding: 2.5rem 1rem;
  text-align: center;
  border-bottom: 2px solid #c9a063;
  box-shadow: 0 4px 20px rgba(10, 20, 40, 0.15);

  .banner-content h1 {
    font-family: "Pacifico", cursive;
    font-size: 2.4rem;
    color: #1DA1F2;
    margin-bottom: 0.5rem;

    @media (max-width: 600px) {
      font-size: 1.8rem;
    }
  }

  .banner-content p {
    font-size: 1.05rem;
    color: #e0d8cc;

    @media (max-width: 600px) {
      font-size: 0.9rem;
    }
  }
`;

const MainContainer = styled.div`
  max-width: 1140px;
  margin: 0 auto;
  padding: 1.5rem 1rem;

  @media (max-width: 600px) {
    padding: 1rem 0.5rem;
  }
`;

const SearchFilterBox = styled.div`
  margin-bottom: 1.2rem;

  .search-input-wrapper {
    position: relative;
    display: flex;
    align-items: center;

    .search-icon {
      position: absolute;
      left: 16px;
      pointer-events: none;
    }

    input {
      width: 100%;
      padding: 0.85rem 2.8rem;
      border-radius: 25px;
      border: 1px solid rgba(201, 160, 99, 0.4);
      background-color: #ffffff;
      font-size: 20px;
      box-shadow: 0 4px 15px rgba(10, 20, 40, 0.04);
      outline: none;
      transition: border-color 0.2s, box-shadow 0.2s;

      &:focus {
        border-color: #1DA1F2;
        box-shadow: 0 4px 20px rgba(29, 161, 242, 0.15);
      }
    }

    .clear-btn {
      position: absolute;
      right: 16px;
      background: none;
      border: none;
      color: #999;
      cursor: pointer;
      font-size: 18px;

      &:hover { color: #333; }
    }
  }
`;

const TabsContainer = styled.div`
  margin-bottom: 1.5rem;
  padding-bottom: 0.5rem;
  border-bottom: 1px solid rgba(201, 160, 99, 0.2);

  .tabs-scroll {
    display: flex;
    gap: 0.5rem;
    overflow-x: auto;
    white-space: nowrap;
    padding-bottom: 6px;
    -webkit-overflow-scrolling: touch;

    &::-webkit-scrollbar {
      height: 4px;
    }
    &::-webkit-scrollbar-thumb {
      background-color: rgba(201, 160, 99, 0.3);
      border-radius: 2px;
    }
  }
`;

const TabButton = styled.button`
  background-color: ${(props) => (props.active ? "#1DA1F2" : "#ffffff")};
  color: ${(props) => (props.active ? "#ffffff" : "#444444")};
  border: 1px solid ${(props) => (props.active ? "#1DA1F2" : "rgba(201, 160, 99, 0.3)")};
  border-radius: 20px;
  padding: 0.5rem 1.1rem;
  font-size: 19px;
  font-weight: 700;
  cursor: pointer;
  flex-shrink: 0;
  transition: all 0.2s;
  box-shadow: ${(props) => (props.active ? "0 2px 8px rgba(29, 161, 242, 0.3)" : "none")};

  &:hover {
    background-color: ${(props) => (props.active ? "#0d8ecf" : "#f5efe6")};
  }
`;

const GridContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 1.25rem;

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;

const FellowCard = styled.div`
  background-color: #ffffff;
  border: 1px solid rgba(201, 160, 99, 0.3);
  border-radius: 12px;
  padding: 1.25rem;
  box-shadow: 0 4px 20px rgba(10, 20, 40, 0.05);
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  transition: transform 0.2s, box-shadow 0.2s;

  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 8px 25px rgba(10, 20, 40, 0.1);
  }
`;

const CardTop = styled.div`
  display: flex;
  gap: 12px;
  align-items: flex-start;
`;

const AvatarWrapper = styled.div`
  position: relative;
  width: 68px;
  height: 68px;
  flex-shrink: 0;

  img {
    width: 100%;
    height: 100%;
    border-radius: 50%;
    object-fit: cover;
    border: 2px solid #c9a063;
  }
`;

const OnlineDot = styled.div`
  position: absolute;
  bottom: 2px;
  right: 2px;
  width: 13px;
  height: 13px;
  background-color: #22c55e;
  border: 2px solid #ffffff;
  border-radius: 50%;
`;

const CardBody = styled.div`
  flex: 1;
`;

const NameRow = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;

  h3 {
    font-size: 23px;
    font-weight: 700;
    color: #0a1428;
    margin: 0;
  }
`;

const VerifiedBadge = styled.span`
  background-color: #1DA1F2;
  color: white;
  border-radius: 50%;
  width: 16px;
  height: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

const Handle = styled.div`
  font-size: 18px;
  color: #1DA1F2;
  font-weight: 700;
  margin-bottom: 0.6rem;
`;

const DetailsMeta = styled.div`
  font-size: 18px;
  color: #444;
  display: flex;
  flex-direction: column;
  gap: 5px;
  margin-bottom: 0.6rem;

  .meta-item {
    display: flex;
    align-items: center;
    gap: 6px;

    svg {
      color: #c9a063;
      flex-shrink: 0;
    }
  }

  .meta-item.uni {
    color: #0a1428;
    font-weight: 700;
  }

  .meta-item.mutual {
    color: #666;
    font-size: 17px;

    svg {
      color: #1DA1F2;
    }
  }
`;

const BioText = styled.p`
  font-size: 19px;
  color: #555;
  line-height: 1.4;
  margin-bottom: 1rem;
`;

const CardFooter = styled.div`
  margin-top: auto;
  padding-top: 0.5rem;
`;

const ConnectButton = styled.button`
  width: 100%;
  padding: 0.6rem 0;
  border-radius: 20px;
  border: none;
  font-weight: 700;
  font-size: 19px;
  cursor: pointer;
  transition: all 0.2s;

  background-color: ${(props) => (props.connected ? "#e8f5fe" : "#1DA1F2")};
  color: ${(props) => (props.connected ? "#1DA1F2" : "#ffffff")};
  border: 1px solid ${(props) => (props.connected ? "#1DA1F2" : "transparent")};
  box-shadow: ${(props) => (props.connected ? "none" : "0 2px 8px rgba(29, 161, 242, 0.25)")};

  &:hover {
    background-color: ${(props) => (props.connected ? "#d0e8fc" : "#0d8ecf")};
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 3rem 1rem;
  background-color: #ffffff;
  border-radius: 12px;
  border: 1px dashed rgba(201, 160, 99, 0.4);
  color: #636466;

  h3 {
    color: #0a1428;
    margin-bottom: 0.5rem;
    font-size: 22px;
    font-weight: 700;
  }
`;
